import { DialogTrigger, Dialog, Modal } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { createVehicle } from '@/api/createVehicle';
import { updateVehicle } from '@/api/updateVehicle';
import Button from '@/components/Button/Button';
import InputText from '@/components/InputText/InputText';
import SelectField from '@/components/SelectField/SelectField';
import { NotificationAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { Vehicle, CreateVehicleRequest } from '@/types/vehicle';

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  afterSubmit: () => void;
  vehicle?: Vehicle;
  offices: Office[];
};

const vehicleTypes = ['ワゴン', 'ミニバン'];

const vehicleCapacities = [
  { value: '3', label: '3人' },
  { value: '4', label: '4人' },
  { value: '5', label: '5人' },
];

export const CreateOrEditVehicleDialog = ({
  isOpen,
  setIsOpen,
  afterSubmit,
  vehicle,
  offices,
}: Props) => {
  const isEdit = vehicle !== undefined;

  const [_, setNotification] = useAtom(NotificationAtom);

  const schema: yup.ObjectSchema<CreateVehicleRequest> = yup
    .object({
      name: yup.string().required('車種は必須です'),
      type: yup.string().oneOf(vehicleTypes).required('車両タイプは必須です'),
      capacity: yup
        .number()
        .oneOf(vehicleCapacities.map((item) => Number(item.value)))
        .required('乗車可能人数は必須です'),
      number: yup.string().required('ナンバープレートは必須です'),
      office_id: yup.number().required('事業所の選択は必須です'),
    })
    .required();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateVehicleRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: vehicle ? vehicle.name : '',
      type: vehicle ? vehicle.type : '',
      capacity: vehicle ? vehicle.capacity : 0,
      number: vehicle ? vehicle.number : '',
      office_id: vehicle ? vehicle.office_id : 0,
    },
  });

  console.log(vehicle);

  const onSubmit: SubmitHandler<CreateVehicleRequest> = async (data) => {
    if (!isEdit) {
      try {
        const result = await createVehicle({
          name: data.name,
          type: data.type,
          capacity: Number(data.capacity),
          number: data.number,
          office_id: Number(data.office_id),
        });
        console.log(result);
        if (result.status === 201) {
          console.log(result.result);
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '車両マスタを登録しました',
            message: '車両マスタを登録しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '車両マスタの登録に失敗しました',
            message: '車両マスタの登録に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '車両マスタの登録に失敗しました',
          message: '車両マスタの登録に失敗しました',
        });
      }
    } else {
      try {
        const result = await updateVehicle(vehicle!.id, {
          id: vehicle!.id,
          name: data.name,
          type: data.type,
          capacity: Number(data.capacity),
          number: data.number,
          office_id: Number(data.office_id),
        });
        if (result.status === 200) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: '車両マスタを更新しました',
            message: '車両マスタを更新しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: '車両マスタの更新に失敗しました',
            message: '車両マスタの更新に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '車両マスタの更新に失敗しました',
          message: '車両マスタの更新に失敗しました',
        });
      }
    }

    setIsOpen(false);
    afterSubmit();
  };

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={() => setIsOpen(false)}
    >
      <Modal className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <Dialog className="w-[600px] rounded-lg border border-gray-400 bg-white p-6 shadow-lg outline-none">
          <div className="flex flex-row justify-between">
            <h1 className="mb-5 text-2xl font-700">
              {isEdit ? '車両マスタ編集' : '車両マスタ登録'}
            </h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-row gap-4">
              <InputText
                label="車種"
                type="text"
                required={true}
                {...register('name')}
              />
            </div>
            <div className="flex flex-row gap-4">
              <SelectField
                name="type"
                label="車両タイプ"
                control={control}
                items={vehicleTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                required={true}
              />
            </div>
            <div className="flex flex-row gap-4">
              <SelectField
                name="capacity"
                label="乗車可能人数（※運転手含めない）"
                control={control}
                items={vehicleCapacities}
                required={true}
              />
            </div>
            <div className="flex flex-row gap-4">
              <InputText
                label="ナンバープレート"
                type="text"
                required={true}
                {...register('number')}
              />
            </div>
            <div className="flex flex-row gap-4">
              <SelectField
                name="office_id"
                label="事業所"
                control={control}
                items={offices.map((office) => ({
                  label: office.name,
                  value: office.id.toString(),
                }))}
                defaultValue={vehicle ? vehicle.office_id.toString() : undefined}
                required={true}
              />
            </div>
            <div className="mt-10 flex flex-row items-center justify-center space-x-2">
              <Button
                type="submit"
                size="lg"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                戻る
              </Button>
              <Button
                type="submit"
                size="lg"
                variant={Object.keys(errors).length > 0 ? 'disabled' : 'solid-fill'}
                disabled={Object.keys(errors).length > 0}
              >
                {isEdit ? '保存' : '登録'}
              </Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
};
